/**
 * tutorOfferAdapter — les offres de cours du masteur (groupes, essais, remplacements) → `UITutorOffer`,
 * et les deux règles de la barre d'onglets qui les concernent (l'onglet visible, sa pastille).
 *
 * Portage littéral de `tutor/proposals/ProposalsTabScreen.js` (`renderProposalItem` pour la carte,
 * `getDialogContent` / `getStartDateInfo` pour la boîte, `renderStatusBadge` pour le statut) et de
 * `tutor/tutorTabScreens.js` (l'onglet `proposals`). Partagé : l'accueil (« Ma journée »), l'onglet
 * des offres et la page d'un lien (`GroupAttributionProposalScreen`, `SubstitutionProposalScreen`).
 * Une offre « Changement de créneau » (`kind = slot_change`) prend son titre, ses lignes et son choix
 * d'horaires de `tutorSlotChangeOfferAdapter`.
 *
 * Les lignes viennent du store, telles que `currentUser.js` les range :
 *  - `group_attribution_proposals` / `trial_attribution_proposals` ← `getGroupAttributionProposals`
 *    (`is_trial` 0 / 1), `GroupAttributionProposalResource` + `GroupAttributionResource` ;
 *  - `substitution_proposals` ← `getSubstitutionProposals`, `SubstitutionProposalResource` ;
 *  - les trois `*_meta` ← `getProposalsCount` (`/user/proposals/counts`), ou la `meta` de la liste.
 *
 * Pas d'import de `data/utility/Utils` : il tire `common/utils/mobileUtils` et `expo-web-browser`. Ses
 * aides lues ici sont recopiées à l'identique plus bas (`formatMinutes`, `formatTimeShort`,
 * `buildPerSessionDisplayStrings`) ; `capitalize` est `capitalizeFirst` des formatters.
 */
import moment from "moment"
import "moment/locale/fr"

import { capitalizeFirst, countLabel } from "../../../ui/formatters/textFormatter"
import {
    type UITutorOffer,
    type UITutorOfferDetail,
    type UITutorOfferKind,
    type UITutorOfferLine,
    type UITutorOfferStatus,
    type UITutorOfferTab,
} from "../../../ui/types/tutorDispoOffres"
import {
    type ApiSlotChange,
    slotChangeCardLines,
    slotChangeCardTitle,
    slotChangeOf,
    toUITutorSlotChange,
} from "./tutorSlotChangeOfferAdapter"

// ── Formes lâches du serveur ────────────────────────────────────────────────

/** `LiveSessionLightResource` : `day_time` en « Monday 17:00 » à l'heure locale, `duration` en minutes. */
type ApiOfferLiveSession = {
    id?: number | null
    day_time?: string | null
    duration?: number | null
}

/** `GroupAttributionResource`, réduit à ce que l'onglet lit. */
type ApiGroupAttribution = {
    status?: string | null
    /** `Y-m-d H:i:s` à l'heure locale (`Tz::convertToLocal`). */
    start_date?: string | null
    comment?: string | null
    is_adhoc?: boolean | null
    is_temporary_absence?: boolean | null
    /** Dates déjà écrites par le serveur (« 4 nov. »). */
    temporary_absence_details?: {
        occurrence_count?: number | null
        first_date?: string | null
        last_date?: string | null
    } | null
    /** « 3 élèves - 4ème - Mathématiques ». */
    school_subjects_classes_string?: string | null
    /** Par id de séance : « 4ème Mathématiques ». */
    school_subjects_classes_per_session?: Record<string, string | null | undefined> | null
    live_sessions?: ApiOfferLiveSession[] | null
    /** Seulement sur une offre `is_adhoc` : les séances existantes où l'élève sera ajouté. */
    existing_sessions?: ApiOfferLiveSession[] | null
    /** `slot_change` : une offre « Changement de créneau », sa charge utile dans `slot_change`. */
    kind?: string | null
    slot_change?: ApiSlotChange | null
    /** Lu par la page d'un lien ; le serveur ne le sert pas aujourd'hui (le titre retombe sur la classe). */
    adhoc_students?: { first_name?: string | null; last_name?: string | null; class?: string | null }[] | null
}

/** `GroupAttributionProposalResource` — une ligne des onglets Groupes et Essais. */
export type ApiGroupAttributionProposal = {
    id?: number | null
    status?: string | null
    targeted?: boolean | null
    /** Horodatage ISO (UTC). */
    created_at?: string | null
    updated_at?: string | null
    group_attribution?: ApiGroupAttribution | null
}

/** `SubstitutionProposalResource` → `SubstitutionResource` → `LiveSessionResource`. */
export type ApiSubstitutionProposal = {
    id?: number | null
    status?: string | null
    created_at?: string | null
    updated_at?: string | null
    substitution?: {
        /** Colonne sans cast : `Y-m-d H:i:s` à l'heure de l'app. */
        date?: string | null
        live_session?: {
            duration?: number | null
            school_subjects_classes_string?: string | null
        } | null
    } | null
}

type ApiProposalsMeta = { pending_count?: number | null; targeted_count?: number | null }

/** `state.data.currentUser.object`, réduit à ce que les offres lisent. */
export type ApiTutorOffersUser = {
    profile?: {
        fixed_missions?: number | null
        replace_missions?: number | null
        accept_trials?: number | null
    } | null
    group_attribution_proposals?: ApiGroupAttributionProposal[] | null
    trial_attribution_proposals?: ApiGroupAttributionProposal[] | null
    substitution_proposals?: ApiSubstitutionProposal[] | null
    group_attribution_proposals_meta?: ApiProposalsMeta | null
    substitution_proposals_meta?: ApiProposalsMeta | null
    trial_attribution_proposals_meta?: ApiProposalsMeta | null
}

// ── Les aides du legacy, recopiées ──────────────────────────────────────────

/** `Utils.formatMinutes` — « 45 min » avec l'espace, là où `formatDuration` des ports n'en met pas. */
const formatMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    if (hours === 0) return `${rest} min`
    return rest === 0 ? `${hours}h` : `${hours}h${String(rest).padStart(2, "0")}`
}

/** `Utils.formatTimeShort` : « 17h », « 17h30 ». */
const formatTimeShort = (m: moment.Moment): string =>
    m.minutes() === 0 ? `${m.hours()}h` : `${m.hours()}h${String(m.minutes()).padStart(2, "0")}`

/** Le `day_time` du serveur est en anglais (« Monday 17:00 »), le jour se dit en français. */
const dayTimeOf = (session: ApiOfferLiveSession) => moment(session.day_time, "dddd HH:mm", "en").locale("fr")

/** `Utils.buildPerSessionDisplayStrings` : « Lundi 17h-18h : 4ème Mathématiques », une par séance connue. */
function perSessionLines(
    sessions: ApiOfferLiveSession[],
    perSession: ApiGroupAttribution["school_subjects_classes_per_session"]
) {
    if (perSession == null) return []
    return sessions
        .filter((session) => session.id != null && perSession[String(session.id)])
        .map((session) => {
            const start = dayTimeOf(session)
            const end = moment(start).add(session.duration ?? 0, "minutes")
            const classSubject = perSession[String(session.id)]
            return `${capitalizeFirst(start.format("dddd"))} ${formatTimeShort(start)}-${formatTimeShort(end)} : ${classSubject}`
        })
}

/** `formatTimeSlots` de l'onglet : « Créneaux : Lundi 17h00-18h00, Jeudi 17h00-18h00 », groupés par jour. */
function formatTimeSlots(sessions: ApiOfferLiveSession[], label = "Créneaux"): string {
    if (sessions.length === 0) return "Aucun créneau défini"
    const byDay = new Map<string, string[]>()
    sessions.forEach((session) => {
        const start = dayTimeOf(session)
        const day = capitalizeFirst(start.format("dddd"))
        const end = moment(start).add(session.duration ?? 0, "minutes")
        byDay.set(day, [...(byDay.get(day) ?? []), `${start.format("HH[h]mm")}-${end.format("HH[h]mm")}`])
    })
    return `${label} : ${[...byDay].map(([day, times]) => `${day} ${times.join(", ")}`).join(", ")}`
}

/** La date d'une carte et d'un statut : « 28/10 à 09:12 », à l'heure de l'appareil. */
const shortDateTime = (iso: string) => moment(iso).format("DD/MM [à] HH:mm")

/** Un tableau du store, ou rien : le réducteur peut y laisser autre chose (`ProposalsTabScreen:43`). */
const rowsOf = <T>(rows: T[] | null | undefined): T[] => (Array.isArray(rows) ? rows : [])

// ── Statut ──────────────────────────────────────────────────────────────────

const KNOWN_STATUSES: readonly UITutorOfferStatus[] = [
    "pending",
    "accepted",
    "rejected",
    "expired",
    "chosen",
    "not_chosen",
    "accepted_canceled",
]

/**
 * Le statut que la ligne MONTRE (`renderStatusBadge` → `finalStatus`) : une offre en attente dont le
 * groupe est fermé est expirée ; un remplacement passé de 30 minutes l'est quel que soit son statut —
 * le legacy le teste ainsi, un remplacement accepté puis passé s'affiche « Expirée ».
 * Un statut que la forme ne connaît pas (`auto_acceptance_revoked`, que `scopeInTutorFeed` ne sert
 * jamais au tuteur) fait tomber la ligne : il n'a pas de chip à peindre.
 */
function resolvedStatus(raw: string | null | undefined, expired: boolean): UITutorOfferStatus | null {
    if (expired) return "expired"
    return KNOWN_STATUSES.find((status) => status === raw) ?? null
}

/** `getStatusDate` : la date de la candidature, seulement sur une ligne acceptée. */
const statusDateLabel = (row: { status?: string | null; updated_at?: string | null }) =>
    row.status === "accepted" && row.updated_at ? shortDateTime(row.updated_at) : undefined

// ── Groupes et essais ───────────────────────────────────────────────────────

const ADHOC_NOTE =
    "Si tu ne l'acceptes pas, l'élève ne sera pas ajouté à tes séances existantes et aucun groupe ne sera créé."

/** « 2 élèves - 5ème - Maths » → « Nouvel élève 5ème - Maths » : le titre d'un nouvel élève ne dit pas le compte. */
function adhocTitle(subjectsClasses: string): string {
    const parts = subjectsClasses.split(" - ")
    const classAndSubject = parts.length > 1 ? parts.slice(1).join(" - ") : ""
    return classAndSubject ? `Nouvel élève ${classAndSubject}` : "Nouvel élève"
}

/**
 * Le titre d'une offre dont le serveur ne dit ni la classe ni la matière (`school_subjects_classes_string`
 * vide : aucun élève lu sur ses séances — le cas d'un essai). Le legacy écrivait « Informations non
 * disponibles » ; ici, ce que l'offre porte encore : sa nature et sa première séance (« Essai du
 * mercredi 14h00 »). « Informations non disponibles » ne reste que s'il n'y a vraiment rien.
 */
function untitledOfferTitle(offer: ApiGroupAttribution, kind: "group" | "trial"): string {
    const first = rowsOf(offer.live_sessions).find((session) => session?.day_time)
    const noun = kind === "trial" ? "Essai" : offer.is_temporary_absence ? "Remplacement" : "Groupe"
    const start = first != null ? dayTimeOf(first) : null
    if (start?.isValid()) return `${noun} du ${start.format("dddd")} ${start.format("HH[h]mm")}`
    return kind === "trial" ? "Séance d'essai" : "Informations non disponibles"
}

/** La carte : `renderProposalItem` (`groupTitle`, `groupInfo`), les emoji 📅 / ➕ portés par `kind`. */
function groupCard(offer: ApiGroupAttribution, kind: "group" | "trial"): { title: string; lines: UITutorOfferLine[] } {
    const subjectsClasses = offer.school_subjects_classes_string
    const newSessions = rowsOf(offer.live_sessions)
    const existingSessions = rowsOf(offer.existing_sessions)
    const details = offer.temporary_absence_details

    if (offer.is_temporary_absence && details) {
        const count = details.occurrence_count ?? 0
        return {
            title: subjectsClasses || untitledOfferTitle(offer, kind),
            lines: [
                {
                    text: `Remplacement temporaire sur la période du ${details.first_date} au ${details.last_date} (${countLabel(count, "séance")})`,
                },
            ],
        }
    }
    if (offer.is_adhoc) {
        const lines: UITutorOfferLine[] = []
        if (existingSessions.length > 0) {
            lines.push({ kind: "existing", text: formatTimeSlots(existingSessions, "Créneaux existants") })
        }
        if (newSessions.length > 0) {
            lines.push({ kind: "new", text: formatTimeSlots(newSessions, "Nouveaux créneaux") })
        }
        return { title: adhocTitle(subjectsClasses || ""), lines }
    }
    return {
        title: subjectsClasses || untitledOfferTitle(offer, kind),
        lines: [{ text: formatTimeSlots(newSessions) }],
    }
}

/**
 * `getStartDateInfo` : « Première séance : Lundi 3 novembre à 17h00 », sauf un remplacement temporaire.
 * La page d'un lien (`fromLink`) a la sienne : sans majuscule, et même sur un remplacement temporaire
 * (le legacy).
 */
function startLabelOf(offer: ApiGroupAttribution, fromLink = false): string | undefined {
    if (!offer.start_date || (!fromLink && offer.is_temporary_absence)) return undefined
    const when = moment(offer.start_date).locale("fr").format("dddd D MMMM [à] HH[h]mm")
    return `Première séance : ${fromLink ? when : capitalizeFirst(when)}`
}

/** La boîte : `getDialogContent`, branche groupe (nouvel élève, ou groupe ordinaire). */
function groupDetail(offer: ApiGroupAttribution): UITutorOfferDetail {
    const subjectsClasses = offer.school_subjects_classes_string
    const newSessions = rowsOf(offer.live_sessions)
    const existingSessions = rowsOf(offer.existing_sessions)
    const startLabel = startLabelOf(offer)

    if (offer.is_adhoc) {
        const lines: UITutorOfferLine[] = []
        if (existingSessions.length > 0) {
            lines.push({
                kind: "existing",
                text: formatTimeSlots(existingSessions, "Créneaux existants où l'élève sera ajouté"),
            })
        }
        if (newSessions.length > 0) {
            lines.push({ kind: "new", text: formatTimeSlots(newSessions, "Nouveaux créneaux à créer") })
        }
        // Pas de commentaire de l'équipe sur un nouvel élève : le legacy ne l'écrit que sur un groupe.
        return { heading: adhocTitle(subjectsClasses || ""), lines, startLabel, note: ADHOC_NOTE }
    }

    let heading = subjectsClasses || undefined
    let lines: UITutorOfferLine[]
    const perSession = perSessionLines(newSessions, offer.school_subjects_classes_per_session)
    if (perSession.length > 0 && !offer.is_temporary_absence) {
        const studentCount = subjectsClasses?.match(/^(\d+\s+élèves?)/)?.[1] ?? ""
        const sessionWord = perSession.length > 1 ? "séances" : "séance"
        if (studentCount) heading = `${studentCount} - ${perSession.length} ${sessionWord}`
        lines = perSession.map((text) => ({ text }))
    } else {
        lines = [{ text: formatTimeSlots(newSessions) }]
    }
    // Le DS préfixe « Commentaire : » lui-même : la valeur brute.
    return { heading, lines, startLabel, comment: offer.comment || undefined }
}

/**
 * Une ligne des onglets Groupes ou Essais. L'onglet décide du `kind` (`is_trial` de la requête), pas
 * la ligne : une offre mixte est servie dans les deux, comme le legacy.
 */
export function toUITutorGroupOffer(
    row: ApiGroupAttributionProposal | null | undefined,
    kind: "group" | "trial" = "group"
): UITutorOffer | null {
    if (row?.id == null) return null
    const offer = row.group_attribution ?? {}
    const status = resolvedStatus(row.status, row.status === "pending" && offer.status === "closed")
    if (status == null) return null
    // « Changement de créneau » : son titre et ses lignes à lui, quelle que soit la forme du groupe ;
    // ouvert, il redit ses lignes et ce qu'il faut savoir avant d'accepter (`dialogNote`).
    const rawSlotChange = slotChangeOf(offer)
    const slotChange = rawSlotChange != null ? toUITutorSlotChange(rawSlotChange) : undefined
    const { title, lines } =
        rawSlotChange != null
            ? {
                  title: slotChangeCardTitle(rawSlotChange),
                  lines: slotChangeCardLines(rawSlotChange).map((text) => ({ text })),
              }
            : groupCard(offer, kind)
    return {
        id: row.id,
        kind,
        slotChange,
        adhoc: offer.is_adhoc === true || undefined,
        temporary: offer.is_temporary_absence === true || undefined,
        status,
        targeted: row.targeted === true || undefined,
        createdLabel: row.created_at ? shortDateTime(row.created_at) : "",
        statusDateLabel: statusDateLabel(row),
        title,
        lines,
        detail: slotChange != null ? { lines, note: slotChange.note } : groupDetail(offer),
    }
}

// ── Remplacements ───────────────────────────────────────────────────────────

/**
 * `formatSubstitutionInfo` (la carte) et la branche remplacement de `getDialogContent` (la boîte) :
 * « Mardi 04 novembre à 18:00 - 1h », puis la classe. Le legacy écrit « undefined » quand la classe
 * manque et « NaNhNaN » quand la durée manque : ici, la ligne ou la durée tombent.
 */
function substitutionLines(substitution: ApiSubstitutionProposal["substitution"]): UITutorOfferLine[] {
    if (!substitution) return [{ text: "Informations non disponibles" }]
    const session = substitution.live_session
    if (!session) return [{ text: "Session non définie" }]
    const when = capitalizeFirst(moment(substitution.date).locale("fr").format("dddd DD MMMM [à] HH:mm"))
    const lines: UITutorOfferLine[] = [
        { text: session.duration != null ? `${when} - ${formatMinutes(session.duration)}` : when },
    ]
    if (session.school_subjects_classes_string) lines.push({ text: session.school_subjects_classes_string })
    return lines
}

/** Une ligne de l'onglet Remplacements. Expirée 30 minutes après la séance (`isSubstitutionExpired`). */
export function toUITutorSubstitutionOffer(row: ApiSubstitutionProposal | null | undefined): UITutorOffer | null {
    if (row?.id == null) return null
    const expired =
        row.status === "expired" ||
        moment(row.substitution?.date ?? undefined)
            .add(30, "minutes")
            .isBefore(moment())
    const status = resolvedStatus(row.status, expired)
    if (status == null) return null
    const lines = substitutionLines(row.substitution)
    return {
        id: row.id,
        kind: "substitution",
        status,
        createdLabel: row.created_at ? shortDateTime(row.created_at) : "",
        statusDateLabel: statusDateLabel(row),
        title: row.substitution?.live_session?.school_subjects_classes_string || "Remplacement",
        lines,
        // La boîte d'un remplacement n'a pas de titre : la date et la classe, comme la carte.
        detail: { lines },
    }
}

// ── La page d'un lien ───────────────────────────────────────────────────────

const LINK_ADHOC_NOTE =
    "Cette proposition est uniquement pour toi. Si tu ne l'acceptes pas, l'élève ne sera pas ajouté à tes séances existantes."

/**
 * Ce que dit la page d'un lien (`GroupAttributionProposalScreen`) — ses propres mots, pas ceux de la
 * boîte de l'onglet : un nouvel élève y est nommé (« Nouvel élève : … (5ème) », `adhoc_students`, que
 * le serveur ne sert pas : le titre retombe alors sur la classe), ses séances À CRÉER d'abord
 * (celles du groupe moins les existantes), puis les existantes ; un groupe ordinaire perd son titre
 * quand le compte d'élèves ne se lit pas, et n'écrit rien quand il n'a pas de séance.
 */
function linkGroupDetail(offer: ApiGroupAttribution): UITutorOfferDetail {
    const liveSessions = rowsOf(offer.live_sessions)
    const existingSessions = rowsOf(offer.existing_sessions)
    const existingIds = existingSessions.map((session) => session.id)
    const newSessions = liveSessions.filter((session) => !existingIds.includes(session.id))
    const subjectsClasses = offer.school_subjects_classes_string || undefined
    const startLabel = startLabelOf(offer, true)

    if (offer.is_adhoc) {
        const students = rowsOf(offer.adhoc_students)
        const heading = students.length
            ? `Nouvel élève : ${students
                  .map((s) => `${s.first_name} ${s.last_name}${s.class ? ` (${s.class})` : ""}`)
                  .join(", ")}`
            : subjectsClasses
        const lines: UITutorOfferLine[] = []
        if (newSessions.length > 0) {
            lines.push({ kind: "new", text: formatTimeSlots(newSessions, "Nouvelles séances à créer") })
        }
        if (existingSessions.length > 0) {
            lines.push({
                kind: "existing",
                text: formatTimeSlots(existingSessions, "Séances existantes où l'élève sera ajouté"),
            })
        }
        return { heading, lines, startLabel, note: LINK_ADHOC_NOTE }
    }

    const perSession = perSessionLines(liveSessions, offer.school_subjects_classes_per_session)
    if (perSession.length > 0) {
        const studentCount = subjectsClasses?.match(/^(\d+\s+élèves?)/)?.[1] ?? ""
        const sessionWord = perSession.length > 1 ? "séances" : "séance"
        return {
            heading: studentCount ? `${studentCount} - ${perSession.length} ${sessionWord}` : undefined,
            lines: perSession.map((text) => ({ text })),
            startLabel,
            comment: offer.comment || undefined,
        }
    }
    return {
        heading: subjectsClasses,
        lines: newSessions.length > 0 ? [{ text: formatTimeSlots(newSessions) }] : [],
        startLabel,
        comment: offer.comment || undefined,
    }
}

/**
 * Une offre de groupe ouverte par son lien (`group-attribution-proposals`), par son id ou son `hash` :
 * la ligne de l'onglet, avec ce que dit la page. Le statut est celui de l'onglet — un groupe fermé est
 * expiré (« Dommage ! », comme une offre déjà répondue).
 */
export function toUITutorGroupOfferFromLink(row: ApiGroupAttributionProposal | null | undefined): UITutorOffer | null {
    const offer = toUITutorGroupOffer(row, "group")
    if (offer == null) return null
    // Un changement de créneau dit la même chose dans la liste et sur la page.
    if (offer.slotChange != null) return offer
    return { ...offer, detail: linkGroupDetail(row?.group_attribution ?? {}) }
}

// ── La barre d'onglets ──────────────────────────────────────────────────────

/**
 * Les onglets de la page des offres, dans l'ordre de `ProposalsTabScreen` (`availableTabs`) :
 * Groupes si `fixed_missions` OU une offre ciblée reçue (`targeted_count`, la même clé que la barre
 * lit), Essais si `accept_trials`, Remplacements si `replace_missions`. Les drapeaux sont des entiers.
 */
export function tutorVisibleOfferKinds(user: ApiTutorOffersUser | null | undefined): UITutorOfferKind[] {
    const kinds: UITutorOfferKind[] = []
    if (user?.profile?.fixed_missions === 1 || (user?.group_attribution_proposals_meta?.targeted_count || 0) > 0) {
        kinds.push("group")
    }
    if (user?.profile?.accept_trials === 1) kinds.push("trial")
    if (user?.profile?.replace_missions === 1) kinds.push("substitution")
    return kinds
}

/**
 * Les onglets de la page des offres et leur compte en attente (« Groupes (3) ») : les libellés de
 * `ProposalsTabScreen` lisent le `pending_count` de chaque `*_meta`, compteur ou liste, le dernier
 * arrivé.
 */
export function tutorOfferTabs(user: ApiTutorOffersUser | null | undefined): UITutorOfferTab[] {
    const pendingOf: Record<UITutorOfferKind, number> = {
        group: user?.group_attribution_proposals_meta?.pending_count || 0,
        trial: user?.trial_attribution_proposals_meta?.pending_count || 0,
        substitution: user?.substitution_proposals_meta?.pending_count || 0,
    }
    return tutorVisibleOfferKinds(user).map((kind) => ({ kind, pendingCount: pendingOf[kind] }))
}

/**
 * La pastille de l'onglet (`tutorTabScreens.js`, `tabBarBadge`) : groupes + remplacements + essais
 * en attente, lus sur les `*_meta` — tous les types, même ceux dont l'onglet est caché, comme le
 * legacy. `0` = pas de pastille (le legacy rend `null`).
 */
export function tutorPendingOffersCount(user: ApiTutorOffersUser | null | undefined): number {
    return (
        (user?.group_attribution_proposals_meta?.pending_count || 0) +
        (user?.substitution_proposals_meta?.pending_count || 0) +
        (user?.trial_attribution_proposals_meta?.pending_count || 0)
    )
}

// ── L'accueil ───────────────────────────────────────────────────────────────

/** L'instant de création, pour trier ; une date illisible passe en dernier. */
const createdAtOf = (row: { created_at?: string | null }) => {
    const value = row.created_at ? moment(row.created_at).valueOf() : NaN
    return Number.isNaN(value) ? -Infinity : value
}

/**
 * L'offre de l'accueil (« Ma journée », `<TutorHomeOffer>`) : une seule, en attente — au statut
 * RÉSOLU, donc ni groupe fermé ni remplacement passé —, prise parmi les types dont l'onglet est
 * visible, pour que « Toutes les offres » ouvre un onglet qui la montre.
 *
 * L'ordre : **une offre ciblée d'abord, puis la plus récente** (`created_at`), tous types confondus.
 * Ciblée d'abord parce que c'est l'ordre du serveur dans l'onglet Groupes (`scopeActionableFirst`) :
 * l'accueil peut ne tenir que la tête de chaque liste (une page d'une ligne), et la règle donne la
 * même offre qu'il tienne la tête ou la liste entière. Entre types, aucun ordre serveur commun :
 * la plus récente, ce que dit la maquette A1.
 *
 * Les listes sont celles que l'onglet a rangées dans le compte (persisté) : elles peuvent dater. Les
 * compteurs, eux, sont relus à chaque ouverture de la coquille (`getProposalsCount`) : un type n'est
 * proposé que si son compteur frais dit encore une offre en attente, et `pendingCount` est la
 * pastille (`tutorPendingOffersCount`) — une liste périmée ne le gonfle pas. Reste possible : une
 * offre d'un type qui en attend encore d'autres, prise par un autre depuis la dernière visite de
 * l'onglet ; « Accepter » ouvre alors l'onglet, qui la relit.
 */
export function tutorHomeOffer(user: ApiTutorOffersUser | null | undefined): {
    offer: UITutorOffer | null
    pendingCount: number
} {
    const pendingOf: Record<UITutorOfferKind, number> = {
        group: user?.group_attribution_proposals_meta?.pending_count || 0,
        trial: user?.trial_attribution_proposals_meta?.pending_count || 0,
        substitution: user?.substitution_proposals_meta?.pending_count || 0,
    }
    const kinds = tutorVisibleOfferKinds(user).filter((kind) => pendingOf[kind] > 0)
    type Candidate = { offer: UITutorOffer; createdAt: number }
    const candidates: Candidate[] = []
    const collect = <T extends { created_at?: string | null }>(
        rows: T[] | null | undefined,
        map: (row: T) => UITutorOffer | null
    ) =>
        rowsOf(rows).forEach((row) => {
            const offer = map(row)
            if (offer?.status === "pending") candidates.push({ offer, createdAt: createdAtOf(row) })
        })

    if (kinds.includes("group")) collect(user?.group_attribution_proposals, (row) => toUITutorGroupOffer(row, "group"))
    if (kinds.includes("trial")) collect(user?.trial_attribution_proposals, (row) => toUITutorGroupOffer(row, "trial"))
    if (kinds.includes("substitution")) collect(user?.substitution_proposals, toUITutorSubstitutionOffer)

    candidates.sort(
        (a, b) =>
            Number(b.offer.targeted === true) - Number(a.offer.targeted === true) ||
            b.createdAt - a.createdAt ||
            b.offer.id - a.offer.id
    )
    return {
        offer: candidates[0]?.offer ?? null,
        pendingCount: pendingOf.group + pendingOf.trial + pendingOf.substitution,
    }
}
